// Libraries
import moment from 'moment'

// Utilities
import isEmpty from '../../utility/isEmpty'
import Language from '../../utility/languageUtil'

import { KeyConstants, PaymentProcessorTypeEnum, RenewEnum } from '../../constants'

export default {
  getLoanIds (state) {
    if (isEmpty(state.loans)) {
      return undefined
    }

    return state.loans.map((loan) => {
      return loan.loanId
    })
  },

  getLoanById: (state) => (loanId) => { // Also accepts loanHeader
    if (isEmpty(state.loans) || isEmpty(loanId)) {
      return undefined
    }

    return state.loans.find(loan => {
      return (loan.loanId ? loan.loanId.toString() === loanId.toString() : false) || (loan.loanHeader ? loan.loanHeader.toString() === loanId.toString() : false)
    })
  },

  getLoanIdByHeader: (state) => (loanHeader) => {
    if (isEmpty(state.loans)) {
      return undefined
    }

    const loan = state.loans.find(loan => {
      return loan.loanHeader.toString() === loanHeader.toString()
    })

    if (!isEmpty(loan)) {
      return loan.loanId
    }
  },

  getStoreIds (state) {
    if (isEmpty(state.loans)) {
      return undefined
    }

    return state.loans.map(loan => {
      return loan.storeId
    })
  },

  /**
   * Gets the lmsId of the active or most recently paid off loan
   */
  getActiveLms (state, getters, rootState) {
    if (isEmpty(state.loans)) {
      if (isEmpty(rootState.customer.lmsReferenceIds) || isEmpty(rootState.customer.customerIds)) {
        return undefined
      }

      return Object.keys(rootState.customer.lmsReferenceIds).find(key => rootState.customer.lmsReferenceIds[key].toString() === Object.keys(rootState.customer.customerIds)[0].toString())
    }

    let lmsId
    const activeLoan = state.loans.find(loan => loan.paidOff === false)

    if (!isEmpty(activeLoan)) {
      lmsId = activeLoan.lmsId
    } else {
      const paidOffLoan = state.loans.filter(loan => loan.paidOff === true).sort((loanA, loanB) => moment(loanA.paidOffDate, 'MM/DD/YYYY').valueOf() - moment(loanB.paidOffDate, 'MM/DD/YYYY').valueOf())[0]

      if (!isEmpty(paidOffLoan)) {
        lmsId = paidOffLoan.lmsId
      }
    }

    return lmsId
  },

  isInvictusLmsId: (state) => (lmsId) => {
    return !isEmpty(lmsId) && lmsId === KeyConstants.lmsIds.Invictus
  },

  isNlsLmsId: (state) => (lmsId) => {
    return !isEmpty(lmsId) && lmsId === KeyConstants.lmsIds.NLS
  },

  isTLXeLmsId: (state) => (lmsId) => {
    return !isEmpty(lmsId) && lmsId === KeyConstants.lmsIds.TLXe
  },

  getRefiTerm: (state, getters, rootState) => (loanId, term) => {
    let useRefiTerminology
    if (isEmpty(getters.getLoanById(loanId))) {
      useRefiTerminology = rootState.payment.loan && rootState.payment.loan.loanId === loanId ? (!isEmpty(rootState.payment.loan.useRefiTerm) ? rootState.payment.loan.useRefiTerm : true) : true
    } else {
      useRefiTerminology = !isEmpty(getters.getLoanById(loanId).useRefiTerm) ? getters.getLoanById(loanId).useRefiTerm : true
    }

    if (useRefiTerminology) {
      switch (term) {
        case RenewEnum.Renew:
        case RenewEnum.Renewal:
          return Language.$t('global.refinance')
        case RenewEnum.Renewing:
          return Language.$t('global.refinancing')
      }
    } else {
      switch (term) {
        case RenewEnum.Renew:
          return Language.$t('global.renew')
        case RenewEnum.Renewal:
          return Language.$t('global.renewal')
        case RenewEnum.Renewing:
          return Language.$t('global.renewing')
      }
    }
  },

  /**
   * Returns true if the current, active application originated from a refinance transaction.
   */
  isCurrentApplicationSourceRefi (state) {
    return !isEmpty(state.currentNlsApplication) && state.currentNlsApplication.isRefiApplication === true
  },

  /**
   * Returns true if the current, active application originated from an add-on transaction.
   */
  isCurrentApplicationSourceAddon (state) {
    return !isEmpty(state.currentNlsApplication) && state.currentNlsApplication.isAddonApplication === true
  },

  getLoanPaymentProcessorType (state) {
    if (!isEmpty(state.loans)) {
      return PaymentProcessorTypeEnum.BridgePay
    } else {
      return PaymentProcessorTypeEnum.None
    }
  },

  getBrandsFromLoans (state) {
    if (!isEmpty(state.loans)) {
      return state.loans.map(loan => loan.brand || loan.brandName)
    }

    return []
  },

  getDisbursementProductId (state, getters, rootState) {
    const paymentState = rootState.payment
    return paymentState.migrateProduct ? paymentState.loan.disburseProductMigration.newProductId : paymentState.loan.productId
  },

  /**
   * Checks if the customer has any active Invictus loans.
   * @returns {Boolean} Returns true if the customer has any active Invictus loans.
   */
  hasActiveInvictusLoan (state, getters) {
    if (isEmpty(state.loans)) {
      return false
    }

    return state.loans.some(loan => getters.isInvictusLmsId(loan.lmsId) && !loan.paidOff)
  }
}
